import { Injectable } from '@angular/core';
import { CompanyShareholderService } from '@app/modules/company-shareholder/services/company-shareholder.service';
import { CompanyShareholder } from '@app/modules/company/models/company-shareholder.model';
import { JobService } from '@app/modules/job/services/job.service';
import { ServiceSupportedCountry } from '@app/modules/service/models/service-supported-country.model';
import {
  CompanyShareholderInput,
  CompanyShareholderJobInput,
  CreateCorporateJobInput,
  JobMatterEnum,
  OfficerCompanyInput,
  OfficerDocumentStatusEnum,
  OfficerPersonInput,
  OfficerType,
  OfficerTypeEnum,
  ServiceEnum,
} from '@generated/graphql';
import { createOfficerForm } from '@shared/classes/officer/officer-form-factory';
import { OfficerInputHelpers } from '@shared/classes/officer/officer-input.helper';
import { startCase } from 'lodash-es';
import { DateTime } from 'luxon';
import { OfficerRequestFormState } from './officer-request-form-state';
import { companyShareholderFactory } from '@app/modules/company/factories/company-shareholder.factory';

@Injectable({
  providedIn: 'any',
})
export class ShareholderOfficerRequestForm extends OfficerRequestFormState {
  public editingShareholder: CompanyShareholder;

  constructor(
    private readonly shareholderService: CompanyShareholderService,
    private readonly officerInputHelpers: OfficerInputHelpers,
    private readonly jobService: JobService,
  ) {
    super();
  }

  public createForm() {
    if (this.selectedOfficerType === OfficerTypeEnum.Nominee) {
      this.createNomineeForm();
      return;
    }

    super.createForm();
    this.scheduleOfficerFormState(true);
  }

  public getFormValue(): CompanyShareholderInput {
    const officerFormValue = this.formInput.officerForm.getRawValue();

    if ('identification' in officerFormValue) {
      return {
        isNominee: false,
        officerType: OfficerTypeEnum.Individual.toString(),
        officerPerson: this.officerInputHelpers.transformOfficerInput(
          officerFormValue,
        ) as OfficerPersonInput,
      };
    }

    return {
      isNominee: false,
      officerType: OfficerTypeEnum.Corporate.toString(),
      officerCompany: this.officerInputHelpers.transformOfficerInput(
        officerFormValue,
      ) as OfficerCompanyInput,
    };
  }

  public normalizeExistingOfficerChoices(): this {
    this.existingOfficerState.normalizeExistingOfficers();

    return this;
  }

  protected setExistingOfficerToForm(officer: OfficerType) {
    if ('identification' in officer.officerProfile) {
      this.patchOfficerPersonFormValue(officer.officerProfile);
    } else if ('representative' in officer.officerProfile) {
      this.patchOfficerCompanyFormValue(officer.officerProfile);
    }

    if (
      this.mode === 'Replace' &&
      this.existingOfficerState.selectedOfficerId
    ) {
      this.updateNeedDocumentState(officer);
    }

    this.formInput.officerForm.updateValueAndValidity();
  }

  public setEditData(officer: CompanyShareholder): this {
    this.editingShareholder = officer;

    const officerProfile = officer.officer.officerProfile;

    if ('identification' in officerProfile) {
      // INFO: officer person
      this.setOfficerPersonOfficerData(officerProfile);
    } else if ('representative' in officerProfile) {
      // INFO: officer company
      this.setOfficerCompanyOfficerData(officerProfile);
    }

    return this;
  }

  private createNomineeForm(): void {
    this.formInput = createOfficerForm(OfficerTypeEnum.Individual);
    this.formInput.createOfficerForm();
    this.scheduleOfficerFormState(false);
  }

  private updateNeedDocumentState(officer: OfficerType): void {
    this.needDocument = this.hasPendingOfficerDocuments(officer);
  }

  private hasPendingOfficerDocuments(officer: OfficerType): boolean {
    return !officer.officerDocuments?.every(
      (document) => document.status === OfficerDocumentStatusEnum.Verified,
    );
  }

  private createUpdateShareholderInput(): CompanyShareholderJobInput {
    const shareholderInput: CompanyShareholderJobInput = {
      shareholder: {
        id: this.editingShareholder.id,
        ...this.getFormValue(),
        effectiveDate: DateTime.fromJSDate(
          this.effectiveDate.value,
        ).toISODate(),
      },
    };

    shareholderInput.documents = this.uploadedDocuments;

    return shareholderInput;
  }

  private updateShareholder(): void {
    const shareholderInput = this.createUpdateShareholderInput();

    if (!this.activeUser.isCustomer()) {
      this.submitExpertShareholderUpdate(shareholderInput);
      return;
    }

    this.submitCustomerShareholderUpdate(shareholderInput);
  }

  private submitExpertShareholderUpdate(
    shareholderInput: CompanyShareholderJobInput,
  ): void {
    this.postStatus$.next('SUBMITTING');

    this.shareholderService.expertUpdateShareholder(shareholderInput).subscribe({
      next: (result) => {
        this.result$.next(companyShareholderFactory.tryCreating(result));
        this.postStatus$.next('SUCCESS');
      },
      error: (error) => {
        this.postStatus$.next('ERROR');
        return error;
      },
      complete: () => this.postStatus$.next('FINISHED'),
    });
  }

  private submitCustomerShareholderUpdate(
    shareholderInput: CompanyShareholderJobInput,
  ): void {
    const jobInput = this.createCustomerShareholderJobInput(shareholderInput);

    this.postStatus$.next('SUBMITTING');

    this.jobService.createCorporateJob(jobInput).subscribe({
      next: (job) => {
        this.createdJob = job;
        this.postStatus$.next('SUCCESS');
      },
      error: (error) => {
        this.postStatus$.next('ERROR');
        return error;
      },
      complete: () => {
        this.postStatus$.next('FINISHED');
      },
    });
  }

  private createCustomerShareholderJobInput(
    shareholderInput: CompanyShareholderJobInput,
  ): CreateCorporateJobInput {
    const serviceName = ServiceEnum.EditShareholder;

    this.jobOrderState.addOrUpdateService(serviceName, 'mainServices');

    const jobInput: CreateCorporateJobInput = {
      serviceName,
      jobInput: {
        matter: startCase(JobMatterEnum.EditShareholder),
        companyId: this.company.id,
        serviceSupportedCountryId: this.serviceSupportedCountry.id,
      },
      shareholderInput,
    };

    jobInput.jobInput.detail = JSON.stringify({
      ...jobInput,
      jobServiceOrder: this.jobOrderState.jobServicesOrder,
      oldShareholder: this.editingShareholder,
    });

    return jobInput;
  }

  public submitForm() {
    if (this.mode === 'Edit') {
      this.updateShareholder();
    }
  }

  public setServiceSupportedCountry(services: ServiceSupportedCountry[]): this {
    this.serviceSupportedCountry = services.find(
      (s) =>
        s.supportedCountry.id === this.countryOfIncorporation.id &&
        s.service.name === ServiceEnum.EditShareholder,
    );

    return this;
  }
}
